import { canUserReadAttachment, canUserRemoveAttachment, isLiveAttachment } from '../selectors';
import type { Attachment, MockCache, StorageFolderId, UserId } from '../types';

import { MY_FILES_FOLDER_ID, attachmentFolderId, homeFolderId, isSystemFolderId } from './system';
import { isGeneralFolder } from './tree';

function isInFolder(attachment: Attachment, folderId: StorageFolderId): boolean {
  const own = attachmentFolderId(attachment);
  return isSystemFolderId(folderId) ? own === null && homeFolderId(attachment) === folderId : own === folderId;
}

function byNameThenId(left: Attachment, right: Attachment): number {
  return left.name.localeCompare(right.name, 'ru') || String(left.id).localeCompare(String(right.id));
}

export function selectFolderFiles(cache: MockCache, folderId: StorageFolderId, viewerId: UserId): Attachment[] {
  const files: Attachment[] = [];
  for (const attachment of cache.attachments.values()) {
    if (isInFolder(attachment, folderId) && isLiveAttachment(cache, attachment) && canUserReadAttachment(cache, viewerId, attachment)) {
      files.push(attachment);
    }
  }
  return files.sort(byNameThenId);
}

export function canUserMoveAttachment(cache: MockCache, userId: UserId, attachment: Attachment): boolean {
  return isLiveAttachment(cache, attachment) && canUserReadAttachment(cache, userId, attachment);
}

export function attachmentMoveRefusal(
  cache: MockCache,
  userId: UserId,
  attachment: Attachment,
  target: StorageFolderId | null,
): string | null {
  if (!canUserMoveAttachment(cache, userId, attachment)) {
    return 'Файл нельзя перенести';
  }
  const folderId = target === homeFolderId(attachment) ? null : target;
  if (folderId === null) {
    return attachment.ownerKind === 'storage' && attachment.uploaderId !== userId ? 'Файл нельзя перенести в «Мои файлы»' : null;
  }
  if (folderId === MY_FILES_FOLDER_ID) {
    return 'Файл нельзя перенести в «Мои файлы»';
  }
  if (isSystemFolderId(folderId)) {
    return 'Файл нельзя перенести в системную папку';
  }
  return isGeneralFolder(cache, folderId) ? null : 'Папка не найдена';
}

export function canUserMoveAttachmentTo(
  cache: MockCache,
  userId: UserId,
  attachment: Attachment,
  target: StorageFolderId | null,
): boolean {
  return attachmentMoveRefusal(cache, userId, attachment, target) === null;
}

export function canUserRenameAttachment(cache: MockCache, userId: UserId, attachment: Attachment): boolean {
  return canUserMoveAttachment(cache, userId, attachment) && canUserRemoveAttachment(cache, userId, attachment);
}

export function canMoveAttachment(cache: MockCache, attachment: Attachment): boolean {
  return canUserMoveAttachment(cache, cache.me, attachment);
}

export function canMoveAttachmentTo(cache: MockCache, attachment: Attachment, target: StorageFolderId | null): boolean {
  return canUserMoveAttachmentTo(cache, cache.me, attachment, target);
}

export function canRenameAttachment(cache: MockCache, attachment: Attachment): boolean {
  return canUserRenameAttachment(cache, cache.me, attachment);
}
